/**
 * batch-71 workflow-sjablonen (FO-533, spec 233) - sjabloon -> run -> stappen ->
 * operationele taken.
 *
 * Gedeeld door de api (motor, routes, seed-sjablonen), de backoffice (beheer,
 * run-overzicht, kaart) en de portal (open voor mij, statuskaart). Veldnamen volgen
 * `ContractMijlpaalSjabloonRegel` (code, volgorde, actief, anker, offsetAantal,
 * offsetEenheid, werkdagCorrectie, tijdstip, voorloopDagen, deadlineAnker,
 * deadlineOffsetAantal/Eenheid, termijnDagen, doelgroepen, escalatieDoelgroepen,
 * titel, omschrijving, checklist), afhankelijkheden volgen
 * `HrBesluitStap.vereisteStappen`. Bewust geen vijfde dialect (ADDENDA punt 9).
 *
 * LET OP de naamgeving: een `OperationeleTaak` (tabel operationele_taken) is iets
 * anders dan een `Taak` (tabel taken, de portal-aggregator). Een stap maakt altijd
 * een OperationeleTaak.
 *
 * Doelgroepen (ADDENDA, gedeelde bestanden 2): er is geen generiek doelgroep-model.
 * `WorkflowDoelgroep` wijst een vast `userIds[]`-veld aan op de singleton
 * `WorkflowInstelling`; wie erin staat beheert de operator (actiepunt), namen staan
 * nooit in code.
 */
import { z } from 'zod'
import { idParam } from './id-validators'
import type { TaakAutoRegelKey } from './operationele-taken'
import { queryBoolean } from './zod-query'

/** `bronModule` van elke operationele taak die de motor aanmaakt. */
export const WORKFLOW_TAAK_BRON_MODULE = 'workflow'

/** Systeem-actor: de motor zelf (geen gebruiker). Zelfde vorm als `system:voertuig-meldingen`. */
export const WORKFLOW_SYSTEEM_ACTOR = 'system:workflow'

// ─── Enums ───────────────────────────────────────────────────────────────────

/** De catalogus (spec §C). Elke gebeurtenis heeft precies één `startWorkflows`-hook. */
export const WORKFLOW_GEBEURTENISSEN = [
  'VOERTUIG_VERKOOP',
  'VOERTUIG_BESTUURDERSWISSEL',
  'VOERTUIG_AANSCHAF_OF_LEASE_START',
  'VOERTUIG_LEASE_EINDE',
  'VOERTUIG_INLEVEREN',
  'VOERTUIG_SCHADE_TOTAL_LOSS',
  'TELEFOON_UITGIFTE',
  'LAPTOP_UITGIFTE',
  'TELEFOON_INNAME',
  'MEDEWERKER_INDIENST',
  'MEDEWERKER_UITDIENST',
  'TANKPAS_VERLOREN_GEBLOKKEERD',
  'KLANT_NIEUW_CONTRACT',
  'GEREEDSCHAPSSET_UITGIFTE',
] as const
export type WorkflowGebeurtenis = (typeof WORKFLOW_GEBEURTENISSEN)[number]

export const WORKFLOW_GEBEURTENIS_LABELS: Record<WorkflowGebeurtenis, string> = {
  VOERTUIG_VERKOOP: 'Voertuig verkopen',
  VOERTUIG_BESTUURDERSWISSEL: 'Bestuurder wisselen',
  VOERTUIG_AANSCHAF_OF_LEASE_START: 'Voertuig aanschaf of lease-start',
  VOERTUIG_LEASE_EINDE: 'Einde lease nadert',
  VOERTUIG_INLEVEREN: 'Voertuig inleveren',
  VOERTUIG_SCHADE_TOTAL_LOSS: 'Total loss na schade',
  TELEFOON_UITGIFTE: 'Telefoon uitgeven',
  LAPTOP_UITGIFTE: 'Laptop uitgeven',
  TELEFOON_INNAME: 'Telefoon innemen',
  MEDEWERKER_INDIENST: 'Medewerker in dienst',
  MEDEWERKER_UITDIENST: 'Medewerker uit dienst',
  TANKPAS_VERLOREN_GEBLOKKEERD: 'Tankpas verloren of geblokkeerd',
  KLANT_NIEUW_CONTRACT: 'Monteur bij nieuwe klant',
  GEREEDSCHAPSSET_UITGIFTE: 'Gereedschapsset uitgeven',
}

export const WORKFLOW_STARTMODI = ['AUTOMATISCH', 'BEVESTIGEN', 'HANDMATIG'] as const
export type WorkflowStartmodus = (typeof WORKFLOW_STARTMODI)[number]
export const WORKFLOW_STARTMODUS_LABELS: Record<WorkflowStartmodus, string> = {
  AUTOMATISCH: 'Automatisch bij de gebeurtenis',
  BEVESTIGEN: 'Na bevestiging',
  HANDMATIG: 'Alleen handmatig',
}

export const WORKFLOW_AFSLUITREGELS = ['ALLE_VERPLICHTE_STAPPEN', 'HANDMATIG'] as const
export type WorkflowAfsluitregel = (typeof WORKFLOW_AFSLUITREGELS)[number]
export const WORKFLOW_AFSLUITREGEL_LABELS: Record<WorkflowAfsluitregel, string> = {
  ALLE_VERPLICHTE_STAPPEN: 'Zodra alle verplichte stappen klaar zijn',
  HANDMATIG: 'De eigenaar sluit af',
}

/** Vaste lijsten op `WorkflowInstelling`, geen generieke groep. */
export const WORKFLOW_DOELGROEPEN = ['WAGENPARK', 'ADMINISTRATIE', 'MAGAZIJN', 'HR'] as const
export type WorkflowDoelgroep = (typeof WORKFLOW_DOELGROEPEN)[number]
export const WORKFLOW_DOELGROEP_LABELS: Record<WorkflowDoelgroep, string> = {
  WAGENPARK: 'Wagenpark',
  ADMINISTRATIE: 'Administratie',
  MAGAZIJN: 'Magazijn',
  HR: 'HR',
}

/**
 * Stapsoorten. Bewust niet het platte `HrBesluitStapType` en niet de status `WACHT`
 * van `HrBesluitStapStatus`. `WACHT_EXTERN` staat als soort en als status in de
 * spec: de soort zegt wat de stap IS, de status (`WorkflowRunStapStatus`) waar hij staat.
 */
export const WORKFLOW_STAP_SOORTEN = ['AUTOMATISCH', 'TAAK', 'WACHT_EXTERN', 'CONTROLE', 'BESLISPUNT'] as const
export type WorkflowStapSoort = (typeof WORKFLOW_STAP_SOORTEN)[number]
export const WORKFLOW_STAP_SOORT_LABELS: Record<WorkflowStapSoort, string> = {
  AUTOMATISCH: 'Automatisch',
  TAAK: 'Taak',
  WACHT_EXTERN: 'Wachten op extern',
  CONTROLE: 'Controle',
  BESLISPUNT: 'Beslispunt',
}

export const WORKFLOW_STAP_ANKERS = ['RUN_START', 'VORIGE_STAP'] as const
export type WorkflowStapAnker = (typeof WORKFLOW_STAP_ANKERS)[number]
export const WORKFLOW_STAP_ANKER_LABELS: Record<WorkflowStapAnker, string> = {
  RUN_START: 'de start van het stappenplan',
  VORIGE_STAP: 'het moment dat de stap opengaat',
}

export const WORKFLOW_OFFSET_EENHEDEN = ['WERKDAGEN', 'DAGEN', 'WEKEN', 'MAANDEN'] as const
export type WorkflowOffsetEenheid = (typeof WORKFLOW_OFFSET_EENHEDEN)[number]
export const WORKFLOW_OFFSET_EENHEID_LABELS: Record<WorkflowOffsetEenheid, string> = {
  WERKDAGEN: 'werkdagen',
  DAGEN: 'dagen',
  WEKEN: 'weken',
  MAANDEN: 'maanden',
}

/** Zelfde waarden als `ContractMijlpaalWerkdagCorrectie`. */
export const WORKFLOW_WERKDAG_CORRECTIES = ['GEEN', 'VORIGE_WERKDAG', 'VOLGENDE_WERKDAG'] as const
export type WorkflowWerkdagCorrectie = (typeof WORKFLOW_WERKDAG_CORRECTIES)[number]

/**
 * Hoe een WACHT_EXTERN-stap zijn bevestiging krijgt.
 *  - MAIL_ANTWOORD: een antwoord op de verstuurde mail (FO-527-patroon), of handmatig;
 *  - HANDMATIG: "bevestigd door ..." met een notitie;
 *  - CHECK: de stap is klaar zodra zijn checks slagen (bv. `melding.bevestigd`).
 */
export const WORKFLOW_WACHT_KANALEN = ['MAIL_ANTWOORD', 'HANDMATIG', 'CHECK'] as const
export type WorkflowWachtKanaal = (typeof WORKFLOW_WACHT_KANALEN)[number]
export const WORKFLOW_WACHT_KANAAL_LABELS: Record<WorkflowWachtKanaal, string> = {
  MAIL_ANTWOORD: 'Antwoord op de mail',
  HANDMATIG: 'Handmatig bevestigd',
  CHECK: 'Een controle uit de lijst',
}

/** Wie een TAAK-stap uitvoert: de doelgroep, of de medewerker om wie het gaat. */
export const WORKFLOW_UITVOERDER_BRONNEN = ['DOELGROEP', 'BETROKKEN_MEDEWERKER'] as const
export type WorkflowUitvoerderBron = (typeof WORKFLOW_UITVOERDER_BRONNEN)[number]
export const WORKFLOW_UITVOERDER_BRON_LABELS: Record<WorkflowUitvoerderBron, string> = {
  DOELGROEP: 'De doelgroep van de stap',
  BETROKKEN_MEDEWERKER: 'De medewerker om wie het gaat',
}

export const WORKFLOW_RUN_STATUSSEN = ['OPEN', 'WACHT', 'AFGEROND', 'GEANNULEERD', 'MISLUKT'] as const
export type WorkflowRunStatus = (typeof WORKFLOW_RUN_STATUSSEN)[number]
export const WORKFLOW_RUN_STATUS_LABELS: Record<WorkflowRunStatus, string> = {
  OPEN: 'Bezig',
  WACHT: 'Wacht op extern',
  AFGEROND: 'Afgerond',
  GEANNULEERD: 'Geannuleerd',
  MISLUKT: 'Mislukt',
}
/** Een run die nog werk vraagt (de dedupe: hooguit één per sjabloon en bron). */
export const WORKFLOW_RUN_LOPEND = ['OPEN', 'WACHT'] as const satisfies readonly WorkflowRunStatus[]

export const WORKFLOW_RUN_STAP_STATUSSEN = [
  'WACHT_OP_VOORGANGER',
  'OPEN',
  'BEZIG',
  'WACHT_EXTERN',
  'AFGEROND',
  'OVERGESLAGEN',
  'NIET_VAN_TOEPASSING',
  'MISLUKT',
] as const
export type WorkflowRunStapStatus = (typeof WORKFLOW_RUN_STAP_STATUSSEN)[number]
export const WORKFLOW_RUN_STAP_STATUS_LABELS: Record<WorkflowRunStapStatus, string> = {
  WACHT_OP_VOORGANGER: 'Wacht op eerdere stap',
  OPEN: 'Open',
  BEZIG: 'Bezig',
  WACHT_EXTERN: 'Wacht op extern',
  AFGEROND: 'Afgerond',
  OVERGESLAGEN: 'Overgeslagen',
  NIET_VAN_TOEPASSING: 'Niet van toepassing',
  MISLUKT: 'Mislukt',
}
/** Klaar: een stap erachter mag open, en hij telt mee als afgehandeld. */
export const WORKFLOW_STAP_KLAAR_STATUSSEN = [
  'AFGEROND',
  'OVERGESLAGEN',
  'NIET_VAN_TOEPASSING',
] as const satisfies readonly WorkflowRunStapStatus[]

/** Hoe een stap gereed werd: nooit stil, altijd zichtbaar wat de bron was (§6.2). */
export const WORKFLOW_GEREED_BRONNEN = ['AUTOMATISCH', 'HANDMATIG', 'TAAK', 'MAIL', 'BESLISSING'] as const
export type WorkflowGereedBron = (typeof WORKFLOW_GEREED_BRONNEN)[number]
export const WORKFLOW_GEREED_BRON_LABELS: Record<WorkflowGereedBron, string> = {
  AUTOMATISCH: 'Automatisch door de controle of actie',
  HANDMATIG: 'Handmatig',
  TAAK: 'De taak is gereed gemeld',
  MAIL: 'Antwoord per mail',
  BESLISSING: 'Beslissing',
}

export const WORKFLOW_BRON_TYPEN = ['VOERTUIG', 'MEDEWERKER', 'UNIEK_ITEM', 'TANKPAS', 'MEDEWERKER_KLANT'] as const
export type WorkflowBronType = (typeof WORKFLOW_BRON_TYPEN)[number]
export const WORKFLOW_BRON_TYPE_LABELS: Record<WorkflowBronType, string> = {
  VOERTUIG: 'Voertuig',
  MEDEWERKER: 'Medewerker',
  UNIEK_ITEM: 'Middel',
  TANKPAS: 'Tankpas',
  MEDEWERKER_KLANT: 'Monteur bij klant',
}

export const WORKFLOW_RUN_AUDIT_ACTIES = [
  'GESTART',
  'STAP_GEOPEND',
  'STAP_AFGEROND',
  'STAP_OVERGESLAGEN',
  'STAP_NIET_VAN_TOEPASSING',
  'STAP_HEROPEND',
  'STAP_MISLUKT',
  'STAP_POGING',
  'STAP_OPNIEUW',
  'STAP_BESLIST',
  'STAP_BEVESTIGD',
  'STAP_HERINNERD',
  'STAP_GEESCALEERD',
  'STAP_TOEGEVOEGD',
  'GEANNULEERD',
  'AFGEROND',
  'MISLUKT',
] as const
export type WorkflowRunAuditActie = (typeof WORKFLOW_RUN_AUDIT_ACTIES)[number]

export const WORKFLOW_START_UITKOMSTEN = [
  'GESTART',
  'OVERGESLAGEN_UIT',
  'OVERGESLAGEN_DEDUPE',
  'OVERGESLAGEN_VOORWAARDE',
  'WACHT_OP_BEVESTIGING',
  'OVERGESLAGEN_HANDMATIG',
  'GEEN_SJABLOON',
  'FOUT',
] as const
export type WorkflowStartUitkomst = (typeof WORKFLOW_START_UITKOMSTEN)[number]
export const WORKFLOW_START_UITKOMST_LABELS: Record<WorkflowStartUitkomst, string> = {
  GESTART: 'Gestart',
  OVERGESLAGEN_UIT: 'Sjabloon staat uit',
  OVERGESLAGEN_DEDUPE: 'Liep al voor deze gebeurtenis',
  OVERGESLAGEN_VOORWAARDE: 'Voorwaarde niet vervuld',
  WACHT_OP_BEVESTIGING: 'Wacht op bevestiging',
  OVERGESLAGEN_HANDMATIG: 'Alleen handmatig te starten',
  GEEN_SJABLOON: 'Geen sjabloon',
  FOUT: 'Fout',
}

// ─── Voorwaarden ─────────────────────────────────────────────────────────────

/**
 * Een voorwaarde op de bron (`voertuig.eigendom in [KOOP]`), als JSON op het sjabloon
 * of de stap. `alle`/`eenVan`/`niet` combineren. Zie `evalueerVoorwaarde` in
 * `@streep/utils`, dat ook de leesbare reden maakt.
 */
export const WORKFLOW_VOORWAARDE_OPERATOREN = ['is', 'isNiet', 'in', 'nietIn', 'gevuld', 'leeg', 'groterDan'] as const
export type WorkflowVoorwaardeOperator = (typeof WORKFLOW_VOORWAARDE_OPERATOREN)[number]

const voorwaardeWaarde = z.union([z.string(), z.number(), z.boolean(), z.null()])

export type WorkflowVoorwaarde =
  | { veld: string; op: WorkflowVoorwaardeOperator; waarde?: string | number | boolean | null | Array<string | number | boolean | null> }
  | { alle: WorkflowVoorwaarde[] }
  | { eenVan: WorkflowVoorwaarde[] }
  | { niet: WorkflowVoorwaarde }

export const workflowVoorwaardeSchema: z.ZodType<WorkflowVoorwaarde> = z.lazy(() =>
  z.union([
    z
      .object({
        veld: z.string().min(1).max(100),
        op: z.enum(WORKFLOW_VOORWAARDE_OPERATOREN),
        waarde: z.union([voorwaardeWaarde, z.array(voorwaardeWaarde)]).optional(),
      })
      .strict(),
    z.object({ alle: z.array(workflowVoorwaardeSchema).min(1) }).strict(),
    z.object({ eenVan: z.array(workflowVoorwaardeSchema).min(1) }).strict(),
    z.object({ niet: workflowVoorwaardeSchema }).strict(),
  ]),
)

/** De velden waar een voorwaarde op kan: wat de bron-context altijd vult. */
export const WORKFLOW_VOORWAARDE_VELDEN: Record<string, string> = {
  'voertuig.eigendom': 'Eigendom van het voertuig',
  'voertuig.type': 'Soort voertuig',
  'voertuig.isLease': 'Het voertuig is lease',
  'voertuig.leaseBijWassink': 'Lease bij de leasemaatschappij die bestuurdersmutaties krijgt',
  'voertuig.heeftVerzekeraarEmail': 'Mailadres verzekeraar ingevuld',
  'tankpassen.aantal': 'Aantal tankpassen in omloop',
  'sleutels.aantal': 'Aantal autosleutels',
  'toewijzing.soort': 'Soort toewijzing (vast of tijdelijk)',
  'employee.bijtelling': 'De medewerker heeft bijtelling',
  'item.categorie': 'Categorie van het middel',
  'item.heeftSim': 'Er hangt een SIM aan het middel',
}

// ─── Stapdefinitie ───────────────────────────────────────────────────────────

const hhmm = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Gebruik UU:MM')
  .nullable()

export const WORKFLOW_TAAK_TYPEN = [
  'VOERTUIG_OPHALEN',
  'VOERTUIG_BRENGEN',
  'GEREEDSCHAP_BRENGEN',
  'GEREEDSCHAP_OPHALEN',
  'MATERIAAL_BRENGEN',
  'MATERIAAL_OPHALEN',
  'AUTOSLEUTEL_OPHALEN',
  'PBM_KLEDING_AFLEVEREN',
  'DOCUMENT_OPHALEN',
  'DOCUMENT_LATEN_TEKENEN',
  'INTERNE_TAAK',
  'SPOEDACTIE_KLANT',
  'OVERIG',
] as const
const WORKFLOW_TAAK_PRIORITEITEN = ['HOOG', 'MIDDEL', 'LAAG'] as const
export const WORKFLOW_BEWIJS_EISEN = ['GEEN', 'OPMERKING', 'FOTO', 'HANDTEKENING', 'KILOMETERSTAND'] as const

/** Een sleutel plus parameters uit de actie- of check-registry. */
export const workflowRegistryVerwijzingSchema = z
  .object({
    sleutel: z.string().min(1).max(100),
    params: z.record(z.string(), z.unknown()).default({}),
  })
  .strict()
export type WorkflowRegistryVerwijzing = z.infer<typeof workflowRegistryVerwijzingSchema>

export const workflowBeslisOptieSchema = z
  .object({
    waarde: z.string().min(1).max(50),
    label: z.string().min(1).max(100),
    /** Deze keuze voert de acties van de stap uit (daarna wacht hij eventueel extern). */
    voertActiesUit: z.boolean().default(false),
    /** Stappen die bij deze keuze zichtbaar worden overgeslagen, met de keuze als reden. */
    slaatOver: z.array(z.string().min(1)).default([]),
  })
  .strict()
export type WorkflowBeslisOptie = z.infer<typeof workflowBeslisOptieSchema>

export const workflowBeslissingSchema = z
  .object({
    vraag: z.string().min(1).max(300),
    opties: z.array(workflowBeslisOptieSchema).min(2),
    /**
     * De bron geeft het antwoord al (§D stap 7: eigen auto): is de voorwaarde vervuld,
     * dan kiest de motor `automatischAntwoord` met audit. Null = altijd een mens.
     */
    automatischAls: workflowVoorwaardeSchema.nullable().default(null),
    automatischAntwoord: z.string().nullable().default(null),
  })
  .strict()
export type WorkflowBeslissing = z.infer<typeof workflowBeslissingSchema>

/**
 * Eén stap, zoals in `WorkflowSjabloonStap` en als bevroren snapshot in
 * `WorkflowRunStap.definitie` (lopende runs houden hun versie, §6.7).
 */
export const workflowStapDefinitieSchema = z
  .object({
    code: z
      .string()
      .min(1, 'Geef de stap een code')
      .max(60)
      .regex(/^[A-Z0-9_]+$/, 'Alleen hoofdletters, cijfers en een liggend streepje'),
    volgorde: z.number().int().min(0),
    actief: z.boolean().default(true),
    soort: z.enum(WORKFLOW_STAP_SOORTEN),
    titel: z.string().trim().min(1, 'Geef de stap een titel').max(200),
    omschrijving: z.string().max(4000).nullable().default(null),
    verplicht: z.boolean().default(true),
    vereisteStappen: z.array(z.string().min(1)).default([]),
    voorwaarde: workflowVoorwaardeSchema.nullable().default(null),
    // Het moment waarop de stap op zijn vroegst opengaat (0 = direct na de voorgangers).
    anker: z.enum(WORKFLOW_STAP_ANKERS).default('VORIGE_STAP'),
    offsetAantal: z.number().int().min(0).max(3650).default(0),
    offsetEenheid: z.enum(WORKFLOW_OFFSET_EENHEDEN).default('DAGEN'),
    werkdagCorrectie: z.enum(WORKFLOW_WERKDAG_CORRECTIES).default('GEEN'),
    tijdstip: hhmm.default(null),
    voorloopDagen: z.number().int().min(0).max(365).default(0),
    // De deadline: eigen anker plus offset, of `termijnDagen` kalenderdagen na opengaan.
    deadlineAnker: z.enum(WORKFLOW_STAP_ANKERS).nullable().default(null),
    deadlineOffsetAantal: z.number().int().min(0).max(3650).default(0),
    deadlineOffsetEenheid: z.enum(WORKFLOW_OFFSET_EENHEDEN).default('WERKDAGEN'),
    termijnDagen: z.number().int().min(0).max(3650).nullable().default(null),
    // Wie.
    doelgroepen: z.array(z.enum(WORKFLOW_DOELGROEPEN)).default([]),
    escalatieDoelgroepen: z.array(z.enum(WORKFLOW_DOELGROEPEN)).default([]),
    uitvoerderBron: z.enum(WORKFLOW_UITVOERDER_BRONNEN).default('DOELGROEP'),
    // Wat.
    acties: z.array(workflowRegistryVerwijzingSchema).default([]),
    checks: z.array(workflowRegistryVerwijzingSchema).default([]),
    // TAAK (ook gebruikt voor de beslis-taak en de "handmatig doen"-taak).
    taakType: z.enum(WORKFLOW_TAAK_TYPEN).default('INTERNE_TAAK'),
    prioriteit: z.enum(WORKFLOW_TAAK_PRIORITEITEN).default('MIDDEL'),
    bewijsEis: z.enum(WORKFLOW_BEWIJS_EISEN).default('GEEN'),
    checklist: z.array(z.string().trim().min(1).max(200)).default([]),
    /** Sleutel in de run-context: `{ label: boolean }` vult de checklist vooraf (de wissel-dialoog). */
    checklistVoorafSleutel: z.string().max(60).nullable().default(null),
    /** Relatief pad met {voertuigId}, {employeeId}, {uniekItemId}, {runId}; `portal:` voor de portal. */
    deeplink: z
      .string()
      .max(300)
      .regex(/^(portal:)?\/(?![\/\\])[^\s\\]*$/, 'Een relatief pad in de app, bijvoorbeeld /materieel/{voertuigId}')
      .nullable()
      .default(null),
    // WACHT_EXTERN (of een AUTOMATISCH-stap die na zijn acties extern wacht).
    wachtKanaal: z.enum(WORKFLOW_WACHT_KANALEN).nullable().default(null),
    herinneringWerkdagen: z.number().int().min(1).max(60).nullable().default(null),
    // BESLISPUNT.
    beslissing: workflowBeslissingSchema.nullable().default(null),
  })
  .strict()
export type WorkflowStapDefinitie = z.infer<typeof workflowStapDefinitieSchema>
export type WorkflowStapDefinitieInput = z.input<typeof workflowStapDefinitieSchema>

export const workflowSjabloonInvoerSchema = z
  .object({
    sleutel: z
      .string()
      .min(1, 'Geef het sjabloon een sleutel')
      .max(80)
      .regex(/^[A-Z0-9_]+$/, 'Alleen hoofdletters, cijfers en een liggend streepje'),
    naam: z.string().trim().min(1, 'Geef het sjabloon een naam').max(150),
    gebeurtenis: z.enum(WORKFLOW_GEBEURTENISSEN),
    actief: z.boolean().default(true),
    omschrijving: z.string().max(4000).nullable().default(null),
    voorwaarden: workflowVoorwaardeSchema.nullable().default(null),
    startmodus: z.enum(WORKFLOW_STARTMODI).default('AUTOMATISCH'),
    afsluitregel: z.enum(WORKFLOW_AFSLUITREGELS).default('ALLE_VERPLICHTE_STAPPEN'),
    eigenaarDoelgroep: z.enum(WORKFLOW_DOELGROEPEN).default('WAGENPARK'),
    stappen: z.array(workflowStapDefinitieSchema).min(1, 'Een stappenplan heeft minstens één stap'),
  })
  .strict()
export type WorkflowSjabloonInvoer = z.infer<typeof workflowSjabloonInvoerSchema>
export type WorkflowSjabloonInvoerRuw = z.input<typeof workflowSjabloonInvoerSchema>

/** Een seed-sjabloon (code): de invoer plus de koppeling aan een bestaande schakelaar. */
export interface WorkflowSjabloonSeed extends WorkflowSjabloonInvoerRuw {
  /** De bestaande `TaakAutoRegel` die dit sjabloon aan- en uitzet (null = eigen schakelaar). */
  autoRegelKey?: TaakAutoRegelKey | null
  /** Omhoog bij een inhoudelijke wijziging; bestaande sjablonen raakt een seed nooit. */
  seedVersie: number
}

// ─── Instellingen ────────────────────────────────────────────────────────────

const userIdLijst = z.array(idParam('gebruiker')).max(100).default([])

export const workflowInstellingInputSchema = z
  .object({
    wagenparkUserIds: userIdLijst,
    administratieUserIds: userIdLijst,
    magazijnUserIds: userIdLijst,
    hrUserIds: userIdLijst,
    opzegMailsActief: z.boolean(),
    opzegAntwoordAdres: z.string().trim().email('Geef een geldig mailadres').max(200).nullable(),
    herinneringWerkdagen: z.number().int().min(1).max(60),
    /** Als tekst met decimale punt: geld is nooit een Float. Null = geen indicatie. */
    brandstofPrijsPerLiter: z
      .string()
      .trim()
      .regex(/^\d{1,3}(\.\d{1,3})?$/, 'Geef een bedrag met een punt, bijvoorbeeld 1.95')
      .nullable(),
  })
  .strict()
export type WorkflowInstellingInput = z.infer<typeof workflowInstellingInputSchema>

export interface WorkflowInstellingDTO {
  wagenparkUserIds: string[]
  administratieUserIds: string[]
  magazijnUserIds: string[]
  hrUserIds: string[]
  /** De namen bij de ids, voor de pickers. */
  gebruikers: Array<{ id: string; naam: string }>
  opzegMailsActief: boolean
  opzegAntwoordAdres: string | null
  herinneringWerkdagen: number
  brandstofPrijsPerLiter: string | null
  magBewerken: boolean
}

// ─── Request-schema's ────────────────────────────────────────────────────────

const redenTekst = z.string().trim().min(3, 'Geef een reden (minstens 3 tekens)').max(1000)

export const workflowRedenSchema = z.object({ reden: redenTekst }).strict()
export type WorkflowRedenInput = z.infer<typeof workflowRedenSchema>

export const workflowBevestigenSchema = z
  .object({ notitie: z.string().trim().min(1, 'Noteer door wie of hoe het is bevestigd').max(1000) })
  .strict()
export type WorkflowBevestigenInput = z.infer<typeof workflowBevestigenSchema>

export const workflowBeslissenSchema = z
  .object({ waarde: z.string().min(1).max(50), toelichting: z.string().trim().max(1000).nullable().default(null) })
  .strict()
export type WorkflowBeslissenInput = z.infer<typeof workflowBeslissenSchema>

export const workflowHandmatigAfrondenSchema = z
  .object({ toelichting: z.string().trim().min(3, 'Leg vast wat er buiten STREEP om is gedaan').max(1000) })
  .strict()
export type WorkflowHandmatigAfrondenInput = z.infer<typeof workflowHandmatigAfrondenSchema>

export const workflowSjabloonActiefSchema = z.object({ actief: z.boolean() }).strict()

export const workflowToepassenSchema = z.object({ bevestigd: z.literal(true) }).strict()

export const workflowVoorbeeldSchema = z
  .object({ bronType: z.enum(WORKFLOW_BRON_TYPEN), bronId: idParam('bron') })
  .strict()
export type WorkflowVoorbeeldInput = z.infer<typeof workflowVoorbeeldSchema>

export const workflowHandmatigStartSchema = z
  .object({ sjabloonId: idParam('sjabloon'), bronType: z.enum(WORKFLOW_BRON_TYPEN), bronId: idParam('bron') })
  .strict()
export type WorkflowHandmatigStartInput = z.infer<typeof workflowHandmatigStartSchema>

export const WORKFLOW_RUN_WEERGAVEN = ['LOPEND', 'WACHT_EXTERN', 'MISLUKT', 'VERLOPEN'] as const
export type WorkflowRunWeergave = (typeof WORKFLOW_RUN_WEERGAVEN)[number]

export const workflowRunLijstQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  gebeurtenis: z.enum(WORKFLOW_GEBEURTENISSEN).optional(),
  status: z.enum(WORKFLOW_RUN_STATUSSEN).optional(),
  weergave: z.enum(WORKFLOW_RUN_WEERGAVEN).optional(),
  eigenaar: z.enum(WORKFLOW_DOELGROEPEN).optional(),
  zoek: z.string().trim().max(100).optional(),
  alleenOpen: queryBoolean().optional(),
  /** Gestart vanaf en tot en met deze dag (Europe/Amsterdam). */
  vanaf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  tot: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
})
export type WorkflowRunLijstQuery = z.infer<typeof workflowRunLijstQuerySchema>

export const workflowOpBronQuerySchema = z.object({
  bronType: z.enum(WORKFLOW_BRON_TYPEN),
  bronId: idParam('bron'),
  alleenLopend: queryBoolean().optional(),
})

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface WorkflowRegistryOptieDTO {
  sleutel: string
  label: string
  omschrijving: string
  paramsUitleg: string | null
  zonderParameters: boolean
}

export interface WorkflowSjabloonOptiesDTO {
  acties: WorkflowRegistryOptieDTO[]
  checks: WorkflowRegistryOptieDTO[]
  voorwaardeVelden: Array<{ veld: string; label: string }>
}

export interface WorkflowSjabloonLijstRijDTO {
  id: string
  sleutel: string
  naam: string
  gebeurtenis: WorkflowGebeurtenis
  actief: boolean
  versie: number
  startmodus: WorkflowStartmodus
  eigenaarDoelgroep: WorkflowDoelgroep
  aantalStappen: number
  lopendeRuns: number
  /** Is er een seed om naar terug te zetten? */
  isSeed: boolean
  /** Staat uit omdat de gekoppelde auto-regel uit staat. */
  autoRegelKey: string | null
  laatsteStart: { uitkomst: WorkflowStartUitkomst; reden: string | null; op: string } | null
}

export interface WorkflowSjabloonDetailDTO extends WorkflowSjabloonInvoer {
  id: string
  versie: number
  isSeed: boolean
  autoRegelKey: string | null
  lopendeRuns: number
  versies: Array<{ versie: number; op: string; door: string | null }>
  magBewerken: boolean
}

export interface WorkflowVoorbeeldStapDTO {
  code: string
  volgorde: number
  soort: WorkflowStapSoort
  titel: string
  verplicht: boolean
  vereisteStappen: string[]
  /** Wat er bij de start mee gebeurt. */
  status: WorkflowRunStapStatus
  reden: string | null
  doelgroepen: WorkflowDoelgroep[]
}

export interface WorkflowVoorbeeldDTO {
  sjabloonId: string
  sjabloonNaam: string
  versie: number
  startmodus: WorkflowStartmodus
  voorwaardeVervuld: boolean
  voorwaardeReden: string | null
  stappen: WorkflowVoorbeeldStapDTO[]
}

export interface WorkflowRunVoortgangDTO {
  klaar: number
  totaal: number
  open: number
  wachtExtern: number
  mislukt: number
  verlopen: number
}

export interface WorkflowRunLijstRijDTO {
  id: string
  gebeurtenis: WorkflowGebeurtenis
  sjabloonNaam: string
  sjabloonVersie: number
  bronType: WorkflowBronType
  bronId: string
  bronLabel: string
  /** Backoffice-pad naar de bron (null als die er niet is). */
  bronHref: string | null
  status: WorkflowRunStatus
  gestartOp: string
  afgerondOp: string | null
  eigenaarDoelgroep: WorkflowDoelgroep
  voortgang: WorkflowRunVoortgangDTO
  openStappen: string[]
}

export interface WorkflowRunLijstKpiDTO {
  lopend: number
  wachtExtern: number
  mislukt: number
  verlopen: number
}

export interface WorkflowRunStapDTO {
  id: string
  code: string
  volgorde: number
  soort: WorkflowStapSoort
  titel: string
  omschrijving: string | null
  verplicht: boolean
  vereisteStappen: string[]
  status: WorkflowRunStapStatus
  /** Waarom overgeslagen of niet van toepassing. */
  reden: string | null
  fout: string | null
  pogingen: number
  gereedBron: WorkflowGereedBron | null
  afgerondOp: string | null
  afgerondDoor: string | null
  deadlineOp: string | null
  verlopen: boolean
  wachtSinds: string | null
  herinnerdOp: string | null
  geescaleerdOp: string | null
  heropendOp: string | null
  bevestigingBron: string | null
  bevestigingNotitie: string | null
  /** De laatste meting per check (leesbaar). */
  checks: Array<{ sleutel: string; label: string; gereed: boolean | null; detail: string }>
  taak: { id: string; titel: string; status: string; uitvoerderNaam: string | null } | null
  doelgroepen: WorkflowDoelgroep[]
  uitkomst: Record<string, unknown> | null
  beslissing: WorkflowBeslissing | null
  /** Wat de ingelogde gebruiker hier mag (server-side bepaald). */
  acties: {
    opnieuw: boolean
    overslaan: boolean
    nietVanToepassing: boolean
    bevestigen: boolean
    beslissen: boolean
    handmatigAfronden: boolean
  }
}

export interface WorkflowRunAuditDTO {
  id: string
  stapSleutel: string | null
  actie: WorkflowRunAuditActie
  vanStatus: string | null
  naarStatus: string | null
  toelichting: string | null
  actor: string
  op: string
}

export interface WorkflowRunDetailDTO extends WorkflowRunLijstRijDTO {
  sjabloonId: string
  afsluitregel: WorkflowAfsluitregel
  /** Snapshot bij de start (kenteken, bestuurder, datum): nooit een BSN of pincode. */
  context: Record<string, unknown>
  gestartDoor: string | null
  annuleerReden: string | null
  vervangenDoorRunId: string | null
  fout: string | null
  stappen: WorkflowRunStapDTO[]
  /** De nieuwste regels van het spoor. */
  audit: WorkflowRunAuditDTO[]
  /** Alle regels van het spoor (de UI meldt het als er meer zijn dan getoond). */
  auditTotaal: number
  magAnnuleren: boolean
  magAfsluiten: boolean
}

/** Kaart "Lopend stappenplan" op de bron (voertuig, medewerker, item). */
export interface WorkflowRunKaartDTO {
  id: string
  naam: string
  gebeurtenis: WorkflowGebeurtenis
  status: WorkflowRunStatus
  gestartOp: string
  voortgang: WorkflowRunVoortgangDTO
  openStappen: Array<{ titel: string; status: WorkflowRunStapStatus; bij: string | null }>
  /** Volledige run openen (alleen met rechten op het overzicht). */
  href: string | null
}

/** Portal: de statuskaart van de eigen wissel. Alleen status, geen ontvangers of fouten. */
export interface MijnWorkflowStatusDTO {
  runId: string
  titel: string
  kenteken: string | null
  gestartOp: string
  klaar: boolean
  regels: string[]
}

export const workflowStartLogQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  uitkomst: z.enum(WORKFLOW_START_UITKOMSTEN).optional(),
})

export interface WorkflowStartLogDTO {
  id: string
  gebeurtenis: WorkflowGebeurtenis
  sjabloonSleutel: string | null
  /** De naam van het sjabloon, als het nog bestaat. */
  sjabloonNaam: string | null
  bronType: string
  bronId: string
  bronLabel: string
  bronHref: string | null
  uitkomst: WorkflowStartUitkomst
  reden: string | null
  runId: string | null
  herhaald: number
  laatstOp: string
}

// ─── Voertuig verkopen (§D) ──────────────────────────────────────────────────

const geldBedrag = z
  .string()
  .trim()
  .regex(/^\d{1,10}(\.\d{1,2})?$/, 'Geef een bedrag met hooguit twee decimalen, bijvoorbeeld 12500.00')

export const voertuigVerkopenSchema = z
  .object({
    verkoopDatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Kies de verkoopdatum'),
    verkochtAanNaam: z.string().trim().min(1, 'Vul de koper in').max(200),
    verkochtAanClientId: idParam('klant').nullable().default(null),
    /** Decimal als tekst: geld is nooit een Float. */
    verkoopPrijs: geldBedrag,
    verkoopKmStand: z.number({ error: 'Vul de kilometerstand bij verkoop in' }).int().min(0).max(5_000_000),
    opmerking: z.string().trim().max(2000).nullable().default(null),
  })
  .strict()
export type VoertuigVerkopenInput = z.infer<typeof voertuigVerkopenSchema>

/** De vinkjes van de wissel-dialoog (§E11). */
export const WORKFLOW_WISSEL_CHECKLIST = [
  'Kentekenbewijs in de auto',
  'Groene kaart in de auto',
  'Tankpas aanwezig',
  'Reservesleutel aanwezig',
] as const
export type WorkflowWisselChecklistLabel = (typeof WORKFLOW_WISSEL_CHECKLIST)[number]

export const workflowWisselChecklistSchema = z.object({
  kentekenbewijs: z.boolean(),
  groeneKaart: z.boolean(),
  tankpas: z.boolean(),
  reservesleutel: z.boolean(),
})
export type WorkflowWisselChecklist = z.infer<typeof workflowWisselChecklistSchema>

/** De checklist uit de dialoog als `{ label: vinkje }` in de run-context. */
export function wisselChecklistAlsLabels(c: WorkflowWisselChecklist): Record<WorkflowWisselChecklistLabel, boolean> {
  return {
    'Kentekenbewijs in de auto': c.kentekenbewijs,
    'Groene kaart in de auto': c.groeneKaart,
    'Tankpas aanwezig': c.tankpas,
    'Reservesleutel aanwezig': c.reservesleutel,
  }
}

/** De checklist als leesbare regel voor `VoertuigConditieRapport.conditie`. */
export function wisselChecklistTekst(c: WorkflowWisselChecklist): string {
  const labels = wisselChecklistAlsLabels(c)
  const aanwezig = WORKFLOW_WISSEL_CHECKLIST.filter((l) => labels[l])
  const ontbreekt = WORKFLOW_WISSEL_CHECKLIST.filter((l) => !labels[l])
  return [
    aanwezig.length > 0 ? `Aanwezig: ${aanwezig.join(', ')}` : null,
    ontbreekt.length > 0 ? `Ontbreekt: ${ontbreekt.join(', ')}` : null,
  ]
    .filter((r): r is string => r !== null)
    .join('. ')
}
